import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import classNames from 'classnames/bind';
import { Tooltip } from '@saucelabs/ds-tooltip';
import Button from '@saucelabs/sl-component-buttons';
import { Loader } from '@saucelabs/sl-component-loader';
import { fromJS } from 'immutable';

import Styles from './DevToolsContainer.styles';
import { devToolsSelector } from '../selectors';
import DevToolsDisabled from './DevToolsDisabled';

const context = classNames.bind(Styles);

const fetchBrowserTabs = (device) => device?.getBrowserTabs();

const useUpdateBrowserTabsWithInterval = (device, delay) => {
  const [browserTabsIds, setBrowserTabs] = useState();
  const intervalRef = useRef();
  const browserTabsIdsRef = useRef();

  browserTabsIdsRef.current = browserTabsIds;

  const updateBrowserTabs = (data) => {
    // sorting is necessary since the backend does not reliably provide data in the same order.
    const sortedData = fromJS(data).sortBy((browserTab) => browserTab.get('id'));
    if (!isEqual(browserTabsIdsRef?.current, sortedData) ||
    browserTabsIdsRef.current === undefined) {
      setBrowserTabs(() => sortedData);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => fetchBrowserTabs(device).then((data) => {
      updateBrowserTabs(data);
    }), delay);
    intervalRef.current = interval;
    return () => {
      clearInterval(intervalRef.current);
    };
  }, [device, delay]);

  return browserTabsIds;
};

// Tips for testing dev tools while poingint to staging
// Replace devToolsUrl hostname with staging's hostname
// e.g. devtoolsUrl = devtoolsUrl?.replace(
//  'local-ui.staging.saucelabs.net:8080', 'app.staging.saucelabs.net');

const DevToolsContainer = ({ devToolsSupported }) => {
  const [activeTab, setActiveTab] = useState();
  const { device } = useSelector(devToolsSelector);
  const updatedBrowserTabs = useUpdateBrowserTabsWithInterval(device, 2000);

  useEffect(() => {
    if (!activeTab) {
      setActiveTab(updatedBrowserTabs?.get(0));
    }
  }, [updatedBrowserTabs]);

  const isLoading = !updatedBrowserTabs || !updatedBrowserTabs.size;
  const devtoolsUrl = device?.getDevtoolsUrl(activeTab?.get('id'));

  return (
    <div
      className={context({
        'loader-wrapper': isLoading,
        'devtools-wrapper': updatedBrowserTabs,
      })}
      data-qa="developer-drawer-dev-tools-container"
    >
      {
        devToolsSupported ? (
          <Loader
            data-qa="developer-drawer-dev-tools-loader"
            fontSize="3.5em"
            lightSpinner={false}
            show={isLoading}
          >
            <div
              className={Styles['tabs-row']}
              data-qa="developer-drawer-dev-tools-tab-button-row"
            >
              {updatedBrowserTabs && updatedBrowserTabs.map((browserTab, index) => (
                <Fragment
                  key={`dev-tools-tab-${browserTab.get('id')}`}
                >
                  <Tooltip
                    delay={300}
                    placement="bottom"
                    title={browserTab.get('url')}
                  >
                    <span>
                      <Button
                        category="primary"
                        className={context(
                          'tab-button',
                          { active: browserTab.get('id') === activeTab?.get('id') },
                        )}
                        onClick={() => setActiveTab(browserTab)}
                        size="sm"
                        type="button"
                      >
                        <span>{`Tab ${index + 1}`}</span>
                      </Button>
                    </span>
                  </Tooltip>
                </Fragment>
              ),
              )}
            </div>
            <div className={Styles['tabs-content']}>
              {device && activeTab && (
                <iframe
                  data-qa="developer-drawer-dev-tools-iframe"
                  height="100%"
                  src={devtoolsUrl}
                  title={`Dev tool ${activeTab.get('url')}`}
                  width="100%"
                />
              )}
            </div>
          </Loader>
        ) : <DevToolsDisabled />
      }
    </div>
  );
};

DevToolsContainer.propTypes = {
  devToolsSupported: PropTypes.bool.isRequired,
};

export default DevToolsContainer;
